'use client';

import { VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import type { WebAppClusterDto } from '@terra/shared-contracts-external-network';
import { Skeleton } from '../../../../components/skeleton';
import { BodyDefault } from '../../../../components/typography';
import { SettingsEditDialog } from '../../../settings/SettingsEditDialog';
import { WebAppClusterCard } from './WebAppClusterCard';
import {
  hasIncompleteWebAppLink,
  isDetectWebAppsConfirmSubmitDisabled,
  type WebAppClusterDecision,
} from './detect-web-apps-confirm';
import type { LinkableWebApplication, WebAppReviewMode } from './web-app-types';

export type WebAppsReviewDialogProps = {
  reviewMode: WebAppReviewMode | null;
  clustersLoading: boolean;
  previewFailed: boolean;
  reviewableClusters: WebAppClusterDto[];
  decisions: Record<string, WebAppClusterDecision>;
  linkChoices: Record<string, string>;
  applications: LinkableWebApplication[];
  isConfirming: boolean;
  onOpenChange: (open: boolean) => void;
  onDecisionChange: (input: {
    similarityKey: string;
    decision: WebAppClusterDecision;
    suggestedApplicationId?: string;
  }) => void;
  onLinkChoiceChange: (input: {
    similarityKey: string;
    applicationId: string;
  }) => void;
  onSubmit: () => void;
};

export function WebAppsReviewDialog({
  reviewMode,
  clustersLoading,
  previewFailed,
  reviewableClusters,
  decisions,
  linkChoices,
  applications,
  isConfirming,
  onOpenChange,
  onDecisionChange,
  onLinkChoiceChange,
  onSubmit,
}: WebAppsReviewDialogProps) {
  const t = useTranslations(
    'designSystem.networkSettings.externalInventory.webApp',
  );
  const tShared = useTranslations(
    'designSystem.networkSettings.externalInventory.shared',
  );
  const modeDescription =
    reviewMode === 'enable'
      ? t('confirmEnableDescription')
      : t('confirmDescription');
  const description =
    !clustersLoading && reviewableClusters.length === 0
      ? t('confirmEmpty')
      : modeDescription;

  return (
    <SettingsEditDialog
      open={reviewMode !== null}
      title={t('confirmTitle')}
      description={description}
      onOpenChange={onOpenChange}
      onSubmit={onSubmit}
      loading={isConfirming}
      submitText={tShared('confirm')}
      isSubmitDisabled={isDetectWebAppsConfirmSubmitDisabled({
        confirmMode: reviewMode,
        clustersLoading,
        previewFailed,
        reviewableCount: reviewableClusters.length,
        hasIncompleteLink: hasIncompleteWebAppLink({
          decisions,
          linkChoices,
        }),
      })}
      contentMaxW="2xl"
      contentMaxHeight="60vh"
    >
      {clustersLoading ? (
        <VStack alignItems="stretch" gap={3}>
          <Skeleton h="88px" borderRadius="lg" />
          <Skeleton h="88px" borderRadius="lg" />
        </VStack>
      ) : previewFailed ? (
        <BodyDefault color="fg.error">{tShared('error')}</BodyDefault>
      ) : (
        <VStack alignItems="stretch" gap={3}>
          {reviewableClusters.map((cluster) => (
            <WebAppClusterCard
              key={cluster.similarityKey}
              cluster={cluster}
              decision={decisions[cluster.similarityKey] ?? 'create'}
              applications={applications}
              linkChoice={linkChoices[cluster.similarityKey]}
              onDecisionChange={(decision) => {
                onDecisionChange({
                  similarityKey: cluster.similarityKey,
                  decision,
                  suggestedApplicationId: cluster.suggestedApplication?.id,
                });
              }}
              onLinkChoiceChange={(applicationId) => {
                onLinkChoiceChange({
                  similarityKey: cluster.similarityKey,
                  applicationId,
                });
              }}
            />
          ))}
        </VStack>
      )}
    </SettingsEditDialog>
  );
}
